import { Method } from 'axios'

export interface ApiErrorResponse {
  error: true
  message: string
  statusCode?: number
  statusText?: string
  data?: any
  headers?: any
  config?: any
}

export function isApiError<T>(response: T | ApiErrorResponse): response is ApiErrorResponse {
  return response != null && typeof response === 'object' && 'error' in response && (response as any).error === true
}

export interface RequestParams {
  method: Method
  url: string
  headers?: Record<string, string>
  onRetry?: () => any
  data?: any
  apiKey?: string
  file?: boolean
  contentType?: string
  sessionId?: string
  clientId?: string
  timeout?: number;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
}

export interface AuthCheckResponse {
  success: boolean
  message?: string
  username?: string
  first?: string
  last?: string
  email?: string
  canPublishAgents?: boolean
  admin?: boolean
}

export interface SignUpRequest {
  username: string
  first: string
  last: string
  password: string
  email: string
  // Optional on client; required by server when signup codes are enabled
  signupCode?: string
}

export interface SignUpResponse {
  success: boolean
  message?: string
}

export interface SignInRequest {
  username: string
  password: string
}

export interface SignInResponse {
  success: boolean
  message?: string
  username?: string
  email?: string
  canPublishAgents?: boolean
  admin?: boolean
}

export interface StripeSubscriptionResponse {
  success: boolean
  customerId: string | null
  subscriptionId: string | null
  subscriptionStatus: string | null
  priceId: string | null
  productId: string | null
  currentPeriodEnd: number | null
  cancelAtPeriodEnd: boolean
  hasAccess: boolean
  billingConfigured: boolean
  message?: string
}

export interface StripePortalSessionResponse {
  success: boolean
  url: string
  message?: string
}

// --- API Key Types ---

export interface ApiKeyMetadata {
  id: string // Added ID
  createdAt: string // ISO 8601 format
  expiresAt: string // ISO 8601 format
}

export interface GetApiKeysResponse {
  success: true
  apiKeys: ApiKeyMetadata[]
}

export interface GenerateApiKeyResponse {
  success: true
  id: string // Added ID
  apiKey: string // The newly generated key (only shown once!)
  expiresAt: string // ISO 8601 format expiration date
}

// Interface for the newly generated API key details (shown only once)
// Note: We might not need the ID here if we only show the key temporarily
export interface NewApiKeyInfo {
  apiKey: string
  expiresAt: string
  // id?: string // Optional: Include if needed for display logic, but likely not
}

export interface DeleteApiKeyResponse {
    success: true
    message?: string
}

// Password Reset Types
export interface RequestPasswordResetRequest {
  email: string
}

export interface RequestPasswordResetResponse {
  success: boolean
  message: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
}

export interface ResetPasswordResponse {
  success: boolean
  message: string
}

export interface SendResultsEmailRequest {
  headline: string
  body: string
}

export interface SendResultsEmailResponse {
  success: boolean
  message?: string
}

/**
 * Admin API types
 */
export interface AdminUserSummary {
  username: string
  email: string
  first: string
  last: string
  createdAt: number
  canPublishAgents: boolean
}

export interface AdminListUsersResponse {
  success: true
  users: AdminUserSummary[]
  limit: number
  offset: number
}

export interface AdminTogglePublishResponse {
  success: true
  username: string
  canPublishAgents: boolean
}

/**
 * Signup Codes (Public + Admin) API types
 */

// Public feature status
export interface SignupCodesStatusResponse {
  enabled: boolean
}

// Admin: create signup codes
export interface CreateSignupCodesRequest {
  restrictionEmail?: string
  maxUses?: number
  expiresInDays?: number
  count?: number // default 1, max 100
}

export interface AdminCreatedSignupCode {
  id: string
  code: string
  restrictionEmail: string | null
  maxUses: number
  expiresAt: string | null // ISO date
  active: boolean
}

export interface CreateSignupCodesResponse {
  success: boolean
  codes: AdminCreatedSignupCode[]
}

// Admin: list signup codes
export interface AdminSignupCode {
  id: string
  code: string
  restrictionEmail: string | null
  maxUses: number
  usedCount: number
  active: boolean
  expiresAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ListSignupCodesResponse {
  success: boolean
  codes: AdminSignupCode[]
  limit: number
  offset: number
}

// Admin: toggle/activate/deactivate
export interface ToggleSignupCodeResponse {
  success: boolean
}

// Admin: delete code
export interface DeleteSignupCodeResponse {
  success: boolean
}

// Admin: invite email
export interface InviteSignupCodeRequest {
  email: string
  maxUses?: number
  expiresInDays?: number
}

export interface InviteSignupCodeResponse {
  success: boolean
  code: string
  link: string
}

// ==============================
// Usage Metrics Types (Appended)
// ==============================

// Usage Summary
export interface UsageSummary {
  inputTokens: number;
  outputTokens: number;
  callCount: number;
  errorCount: number;
  durationMsTotal: number;
  durationMsAvg: number;
}
export interface UsageSummaryResponse {
  success: true;
  data: UsageSummary;
}

// Usage by Model
export interface UsageByModelRow {
  modelName: string;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
  errorCount: number;
  durationMsTotal: number;
}
export interface UsageByModelResponse {
  success: true;
  data: UsageByModelRow[];
}

// Timeseries (not required for the initial chart, but useful if needed)
export interface UsageTimeseriesRow {
  bucketStartMs: number;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
  errorCount: number;
  durationMsTotal: number;
}
export interface UsageTimeseriesResponse {
  success: true;
  data: UsageTimeseriesRow[];
}

// Billing
export interface BillingModelRow {
  modelName: string;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
}
export interface BillingModelBucketRow {
  modelName: string;
  bucketStartMs: number;
  inputTokens: number;
  outputTokens: number;
  callCount: number;
}
export type BillingResponse =
  | { success: true; data: BillingModelRow[] }
  | { success: true; data: BillingModelBucketRow[] };

// Optional helper type
export type UsageWindow = { start: string; end: string } | { last: string };

// ==============================
// User Settings (Frontend DTOs)
// ==============================

export interface UtilityAgentsSettingsDTO {
  titleAgent: string | null;
  configAgent: string | null;
}

export interface UserSettingsDTO {
  userId: string;
  utilityAgents: UtilityAgentsSettingsDTO;
  updatedAt: number;
}

export interface GetUserSettingsResponseDTO {
  success: boolean;
  settings?: UserSettingsDTO;
  message?: string;
}

export interface UpdateUserSettingsRequestDTO {
  utilityAgents?: Partial<UtilityAgentsSettingsDTO>;
}

export interface UpdateUserSettingsResponseDTO {
  success: boolean;
  settings: UserSettingsDTO;
}

// ==============================
// Admin Account Provisioning Types
// ==============================

export interface ProvisionModelPayload {
  name: string
  description: string
  providerKey: string
  model: string
}

export interface ProvisionAgentPayload {
  name: string
  description: string
  systemPrompt: string
  model: string // References the model name from ProvisionModelPayload
  tools?: string[] // MCP server names
  selectedFunctions?: {
    [serverName: string]: string[] // MCP server → function names
  }
}

export interface AdminProvisionRequest {
  userId: string
  provider?: string // Provider key
  apiKey?: string
  url?: string
  overwriteProvider?: boolean
  models: ProvisionModelPayload[]
  agents: ProvisionAgentPayload[]
}

export interface AdminProvisionResponse {
  success: boolean
  message?: string
}

export interface AdminProvisioningStateModel {
  name: string
  providerKey: string
  model: string
}

export interface AdminProvisioningStateAgent {
  name: string
}

export interface AdminProvisioningStateResponse {
  success: true
  userId: string
  providers: string[]
  models: AdminProvisioningStateModel[]
  agents: AdminProvisioningStateAgent[]
}

// ==============================
// Admin MCP Server Request Types
// ==============================

export type AdminMcpServerRequestStatus = 'pending' | 'approved' | 'rejected' | 'installed'

export interface AdminMcpServerRequest {
  id: string
  requestType?: 'platform_install' | 'external_removal'
  serverName: string
  reason: string
  links: string[]
  notes: string
  status: AdminMcpServerRequestStatus
  requestedBy: string
  requestedByEmail: string
  reviewedBy?: string | null
  reviewedAt?: string | null
  reviewNotes?: string | null
  createdAt: string
  updatedAt: string
}

export interface AdminListMcpServerRequestsResponse {
  success: boolean
  requests: AdminMcpServerRequest[]
  limit: number
  offset: number
  total: number
}

export interface AdminUpdateMcpServerRequestPayload {
  status: 'pending' | 'approved' | 'rejected'
  reviewNotes?: string
}

export interface AdminUpdateMcpServerRequestResponse {
  success: boolean
  request: AdminMcpServerRequest
}

export interface AdminSharedServerDeleteImpact {
  serverName: string
  source: 'platform' | 'external'
  authMode: 'none' | 'oauth2'
  transportType: 'stdio' | 'streamable_http'
  installedUsers: number
  connectedAuthUsers: number
  oauthTokenUsers: number
  usersWithSavedSecrets: number
  activeSessionUsers: number
}

export interface AdminSharedServerDeleteImpactResponse {
  success: boolean
  impact: AdminSharedServerDeleteImpact
}
